import React, { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./auth";
import { API_URL } from "src/utilities";
import { getRoutes } from "src/utilities/routes";
import { TypeUser } from "src/interface/enum/TypeUser";
import { IServiceGroup } from "src/interface/IServiceGroup";
import { IService } from "src/interface/IService";
import { IAppUser } from "src/interface/IUser";

// ==================== تعریف Interface ها ====================
interface DashboardDataContextType {
  // داده‌های اصلی
  serviceGroups: IServiceGroup[] | undefined;
  services: IService[] | undefined;
  customers: IAppUser[] | undefined;
  editors: IAppUser[] | undefined;

  // وضعیت‌های loading
  isLoadingServiceGroups: boolean;
  isLoadingServices: boolean;
  isLoadingCustomers: boolean;
  isLoadingEditors: boolean;

  // توابع refetch
  refetchServiceGroups: () => void;
  refetchServices: () => void;
  refetchCustomers: () => void;
  refetchEditors: () => void;

  // داده‌های پردازش شده
  customersOptions: { label: string; value: string | undefined }[];
  editorsOptions: { label: string; value: string | undefined }[];
  parentCustomersOptions: { label: string; value: string | undefined }[];
}

// ==================== ایجاد Context ====================
const DashboardDataContext = createContext<
  DashboardDataContextType | undefined
>(undefined);

// ==================== Provider Component ====================
interface DashboardDataProviderProps {
  children: ReactNode;
}

export const DashboardDataProvider: React.FC<DashboardDataProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const Routes = getRoutes();

  // ==================== تابع fetch مشترک ====================
  const fetchData = React.useCallback(
    (url: string) =>
      fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      }).then((res) => res.json()),
    [user?.token]
  );

  // ==================== درخواست‌های API ====================
  const {
    data: serviceGroups,
    isLoading: isLoadingServiceGroups,
    refetch: refetchServiceGroups,
  } = useQuery<IServiceGroup[]>({
    queryKey: ["ServiceGroupsData"],
    queryFn: () => fetchData(Routes.ServiceGroupsRoutes),
    staleTime: 5 * 60 * 1000, // 5 دقیقه
    gcTime: 10 * 60 * 1000, // 10 دقیقه
    enabled: !!user?.token, // فقط زمانی که token موجود است
  });

  const {
    data: services,
    isLoading: isLoadingServices,
    refetch: refetchServices,
  } = useQuery<IService[]>({
    queryKey: ["ServicesData"],
    queryFn: () => fetchData(Routes.ServicesRoutes),
    staleTime: 5 * 60 * 1000, // 5 دقیقه
    gcTime: 10 * 60 * 1000, // 10 دقیقه
    enabled: !!user?.token, // فقط زمانی که token موجود است
  });

  // لیست سبک مشتریان (getByRole/.../simple شامل parentRef برای parentCustomersOptions)
  const {
    data: customers,
    isLoading: isLoadingCustomers,
    refetch: refetchCustomers,
  } = useQuery<IAppUser[]>({
    queryKey: ["CustomersDataSimple"],
    queryFn: () =>
      fetchData(
        API_URL + "user/getByRole/" + TypeUser.CUSTOMER + "/simple"
      ),
    staleTime: 5 * 60 * 1000, // 5 دقیقه
    gcTime: 10 * 60 * 1000, // 10 دقیقه
    enabled: !!user?.token, // فقط زمانی که token موجود است
  });

  const {
    data: editors,
    isLoading: isLoadingEditors,
    refetch: refetchEditors,
  } = useQuery<IAppUser[]>({
    queryKey: ["EditorsDataSimple"],
    queryFn: () =>
      fetchData(API_URL + "user/getByRole/" + TypeUser.EDITOR + "/simple"),
    staleTime: 5 * 60 * 1000, // 5 دقیقه
    gcTime: 10 * 60 * 1000, // 10 دقیقه
    enabled: !!user?.token, // فقط زمانی که token موجود است
  });

  // ==================== پردازش داده‌ها ====================
  const customersOptions = React.useMemo(() => {
    if (!customers) return [];
    return customers.map((item) => ({
      label: `${item?.fName} ${item?.lName}`,
      value: item.id,
    }));
  }, [customers]);

  const editorsOptions = React.useMemo(() => {
    if (!editors) return [];
    return editors.map((item) => ({
      label: `${item?.fName} ${item?.lName}`,
      value: item.id,
    }));
  }, [editors]);

  const parentCustomersOptions = React.useMemo(() => {
    if (!customers) return [];
    return customers
      .filter((c) => c.parentRef === null)
      .map((item) => ({
        label: `${item?.fName} ${item?.lName}`,
        value: item.id,
      }));
  }, [customers]);

  // ==================== مقداردهی Context ====================
  const value: DashboardDataContextType = {
    serviceGroups,
    services,
    customers,
    editors,
    isLoadingServiceGroups,
    isLoadingServices,
    isLoadingCustomers,
    isLoadingEditors,
    refetchServiceGroups,
    refetchServices,
    refetchCustomers,
    refetchEditors,
    customersOptions,
    editorsOptions,
    parentCustomersOptions,
  };

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
};

// ==================== Hook برای استفاده از Context ====================
export const useDashboardData = () => {
  const context = useContext(DashboardDataContext);
  if (context === undefined) {
    throw new Error(
      "useDashboardData must be used within a DashboardDataProvider"
    );
  }
  return context;
};
